/**
 * Focus management for overlays the theme does not render itself.
 *
 * The cookie consent prompt (Termly) and the cart flyout (Rebuy) are injected by
 * third-party scripts, so their markup cannot be restructured. Both open over the page
 * without taking the keyboard with them:
 *
 * - The consent prompt is written as the first element of <body> but is painted at the
 *   bottom of the viewport, so its controls come first in the tab sequence and last on
 *   screen (WCAG 2.4.3), and nothing moves focus into it when it appears.
 * - The cart flyout leaves the page behind it fully tabbable, so Tab walks out of the
 *   open drawer into content covered by the overlay, and focus is dropped on <body>
 *   when the drawer closes.
 *
 * Both are handled the same way here: watch for the overlay, move focus into it, keep
 * focus inside while it is open, and hand focus back to whatever opened it.
 */
(() => {
  const SCRIPT = document.currentScript;

  const OVERLAYS = [
    {
      name: 'cookie-consent',
      container: '#termly-code-snippet-support',
      panel: '[role="alertdialog"]:not([class*="termly-styles-modal"]), .t-consentPrompt',
      autoFocus: true,
    },
    {
      name: 'cookie-preferences',
      container: '#termly-code-snippet-support',
      panel: '[class*="termly-styles-modal"]',
      autoFocus: true,
      labelFrom: '[class*="preferenceTitle"]',
      label: 'Cookie preferences',
      closeSelector: '.t-closeModal',
      inertBackground: true,
    },
    {
      name: 'cart-flyout',
      container: '#rebuy-cart',
      panel: '.rebuy-cart__flyout',
      autoFocus: false,
      label: SCRIPT?.dataset.cartLabel,
      closeSelector: '.rebuy-cart__flyout-close, [data-rebuy-cart-close]',
      opener: '.cart-drawer-button',
    },
  ];

  const isVisible = (element) => {
    if (!element || !element.isConnected) return false;
    if (element.getClientRects().length === 0) return false;

    const styles = window.getComputedStyle(element);

    return styles.visibility !== 'hidden' && styles.opacity !== '0';
  };

  class OverlayFocusManager {
    constructor(config) {
      this.config = config;
      this.panel = null;
      this.openerElement = null;
      this.onKeydown = null;
      this.trapped = false;

      this.sync();
    }

    findPanel() {
      const container = document.querySelector(this.config.container);
      if (!container) return null;

      const panel = container.querySelector(this.config.panel);

      return isVisible(panel) ? panel : null;
    }

    sync() {
      const panel = this.findPanel();
      if (panel === this.panel) return;

      if (this.panel) this.release();
      if (panel) this.capture(panel);
    }

    capture(panel) {
      this.panel = panel;

      const active = document.activeElement;
      const fallbackOpener = this.config.opener ? document.querySelector(this.config.opener) : null;
      this.openerElement = active === document.body || panel.contains(active) ? fallbackOpener : active;

      if (!panel.getAttribute('role')) panel.setAttribute('role', 'dialog');

      panel.setAttribute('aria-modal', 'true');

      if (!panel.getAttribute('aria-label') && !panel.getAttribute('aria-labelledby')) {
        const title = this.config.labelFrom && panel.querySelector(this.config.labelFrom);
        const label = (title && title.textContent.trim()) || this.config.label;
        if (label) panel.setAttribute('aria-label', label);
      }

      if (this.config.inertBackground) this.setBackgroundInert(true);

      if (this.config.closeSelector) {
        this.onKeydown = (event) => {
          if (event.key !== 'Escape' && event.code !== 'Escape') return;

          panel.querySelector(this.config.closeSelector)?.click();
        };
        document.addEventListener('keydown', this.onKeydown);
      }

      const focusables = FoxTheme.a11y.getFocusableElements(panel);

      if (focusables.length === 0) return;

      const shouldMoveFocus = this.config.autoFocus || !panel.contains(active);

      FoxTheme.a11y.trapFocus(panel, shouldMoveFocus ? focusables[0] : active);
      this.trapped = true;
    }

    setBackgroundInert(on) {
      if (on) {
        FoxTheme.a11y.lockBackground(this, [document.querySelector(this.config.container)]);
      } else {
        FoxTheme.a11y.unlockBackground(this);
      }
    }

    release() {
      if (this.onKeydown) {
        document.removeEventListener('keydown', this.onKeydown);
        this.onKeydown = null;
      }

      if (this.config.inertBackground) this.setBackgroundInert(false);

      // Only release a trap this manager actually pushed, and release it by identity so
      // overlays closing out of order do not pop each other off the stack.
      if (this.trapped) {
        const returnTo = this.openerElement && this.openerElement.isConnected ? this.openerElement : null;

        FoxTheme.a11y.removeTrapFocus(returnTo, this.panel);
        this.trapped = false;
      }

      this.panel = null;
      this.openerElement = null;
    }
  }

  const init = () => {
    if (!window.FoxTheme || !FoxTheme.a11y) return;

    const managers = OVERLAYS.map((config) => new OverlayFocusManager(config));

    // One observer for every overlay: three subtree observers on <body> was the single
    // most expensive thing on the page.
    let pending = null;
    const scheduleSync = () => {
      if (pending) return;

      pending = requestAnimationFrame(() => {
        pending = null;
        managers.forEach((manager) => manager.sync());
      });
    };

    new MutationObserver(scheduleSync).observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'aria-hidden'],
    });
  };

  // theme.js defines FoxTheme.a11y and both scripts are deferred, so it is there in
  // practice; the poll only covers a third-party script reordering them.
  const start = () => {
    if (window.FoxTheme && FoxTheme.a11y) {
      init();
      return;
    }

    let attempts = 0;
    const timer = setInterval(() => {
      if ((window.FoxTheme && FoxTheme.a11y) || ++attempts > 40) {
        clearInterval(timer);
        init();
      }
    }, 100);
  };

  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', start) : start();
})();
